// Layers + size popover (minimap-design.md §6, M4): checkbox toggles for the
// territory / fog / units layers and S/M/L size presets. Keyboard-closable.

import { useEffect, useRef } from 'react';
import { cn } from '../lib/utils.js';
import { type MinimapLayers, type MinimapSize, SIZE_REM } from './minimapPrefs.js';
import type { MinimapLabels } from './types.js';

export interface MinimapLayersPopoverProps {
  readonly id: string;
  readonly labels: MinimapLabels;
  readonly layers: MinimapLayers;
  readonly size: MinimapSize;
  readonly onToggleLayer: (layer: keyof MinimapLayers) => void;
  readonly onSetSize: (size: MinimapSize) => void;
  readonly onClose: () => void;
}

// Rows and size buttons keep a 44 px touch target on phones (spec §6:
// "touch targets >= 44 px for the buttons"). The row itself is the label,
// so it stretches the hit area over the whole line.
const ROW =
  'flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded px-2 py-1 text-[11px] text-foreground hover:bg-accent/60 max-md:min-h-11 max-md:py-2 max-md:text-xs';

export function MinimapLayersPopover({
  id,
  labels,
  layers,
  size,
  onToggleLayer,
  onSetSize,
  onClose,
}: MinimapLayersPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const checkbox = (key: keyof MinimapLayers, label: string, checked: boolean) => {
    return (
      <label key={key} className={ROW}>
        <span>{label}</span>
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggleLayer(key)}
          data-testid={`minimap-layer-${key}`}
          className="size-3.5 cursor-pointer accent-current max-md:size-5"
        />
      </label>
    );
  };

  const sizeButton = (key: MinimapSize, label: string) => (
    <button
      type="button"
      key={key}
      onClick={() => onSetSize(key)}
      aria-pressed={size === key}
      title={`${label} (${SIZE_REM[key]})`}
      data-testid={`minimap-size-${key}`}
      className={cn(
        'min-w-7 cursor-pointer rounded border border-border/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-foreground hover:bg-accent',
        'max-md:flex max-md:min-h-11 max-md:min-w-11 max-md:items-center max-md:justify-center max-md:text-xs',
        size === key && 'bg-accent',
      )}
    >
      {key}
    </button>
  );

  return (
    <div
      ref={ref}
      id={id}
      tabIndex={-1}
      // biome-ignore lint/a11y/useSemanticElements: popover layout mirrors the legend, not a native dialog
      role="dialog"
      aria-label={labels.layers}
      data-testid="minimap-layers"
      data-appdom-id="minimap-layers"
      data-appdom-type="Popover"
      data-appdom-scope="aside"
      data-appdom-label={labels.layers}
      className="absolute top-1 right-1 z-20 w-44 rounded-md border border-border/70 bg-card/95 p-1.5 shadow-lg backdrop-blur"
    >
      <p className="px-2 pt-0.5 pb-1 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
        {labels.layers}
      </p>
      {checkbox('territory', labels.layerTerritory, layers.territory)}
      {checkbox('fog', labels.layerFog, layers.fog)}
      {checkbox('units', labels.layerUnits, layers.units)}
      {checkbox('rivers', labels.layerRivers, layers.rivers)}
      {checkbox('relief', labels.layerRelief, layers.relief)}
      <div className="mt-1 flex items-center justify-between gap-2 border-t border-border/50 px-2 pt-1.5 pb-0.5 max-md:min-h-11">
        <span className="text-[11px] text-foreground max-md:text-xs">{labels.size}</span>
        <span className="flex gap-1">
          {sizeButton('s', labels.sizeS)}
          {sizeButton('m', labels.sizeM)}
          {sizeButton('l', labels.sizeL)}
        </span>
      </div>
    </div>
  );
}
